import { useId } from "react";

export interface SliderProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  /** Text shown next to the label, e.g. "-18.4 dB". Numeric value is always shown. */
  displayValue?: string;
  unit?: string;
  disabled?: boolean;
  hint?: string;
  onChange: (value: number) => void;
}

/**
 * All controls carry a visible label and a numeric readout, are keyboard
 * operable, and expose aria-valuetext so state is never conveyed by colour or
 * position alone (docs/acceptance-criteria.md §13).
 */
export function Slider({
  label,
  value,
  min,
  max,
  step = 1,
  displayValue,
  unit,
  disabled = false,
  hint,
  onChange,
}: SliderProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const readout = displayValue ?? `${value}${unit ?? ""}`;

  return (
    <div className="control">
      <div className="control__header">
        <label className="control__label" htmlFor={id}>
          {label}
        </label>
        {/* A plain span, not <output>: <output> has an implicit role="status",
            which would make every slider readout its own live region and
            compete with the transport status. The value is already exposed to
            assistive tech through aria-valuetext on the input below. */}
        <span className="control__value">{readout}</span>
      </div>
      <input
        id={id}
        className="control__range"
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        disabled={disabled}
        aria-valuetext={readout}
        aria-describedby={hint === undefined ? undefined : hintId}
        onChange={(event) => {
          onChange(Number(event.target.value));
        }}
      />
      {hint !== undefined && (
        <p className="control__hint" id={hintId}>
          {hint}
        </p>
      )}
    </div>
  );
}
